import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Info,
  Calendar,
  Clock,
  MapPin,
  Users,
  Link as LinkIcon,
  HelpCircle,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import {
  useEvent,
  useUpdateEvent,
  type CreateEventData,
  type EventTranslation,
  type EventVideo,
} from "../../../services/eventService";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import ImageUpload from "../../../components/common/ImageUpload";
import DateTimePicker from "../../../components/ui/DateTimePicker";
import { ACCEPTABLE_LANGUAGES } from "../../../constants/languages";

const EventEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();

  const {
    data: event,
    isLoading: isLoadingEvent,
    isError: isErrorEvent,
  } = useEvent(Number(id), {
    admin: true,
  });
  const updateEventMutation = useUpdateEvent();

  const [formData, setFormData] = useState<CreateEventData>({
    starts_at: "",
    ends_at: "",
    venue: "",
    ics_uid: "",
    status: "upcoming",
    category: "",
    duration: "",
    organizer: "",
    attendees: 0,
    registration_link: "",
    detailed_description: "",
    objectives: [],
    highlights: [],
    outcomes: [],
    event_report: "",
    gallery: [],
    videos: [],
    translations: ACCEPTABLE_LANGUAGES.map((lang) => ({
      locale: lang,
      title: "",
      description: "",
      slug: "",
      detailed_description: "",
      objectives: [],
      highlights: [],
      outcomes: [],
      event_report: "",
    })),
  });

  const [currentObjective, setCurrentObjective] = useState("");
  const [currentHighlight, setCurrentHighlight] = useState("");
  const [currentOutcome, setCurrentOutcome] = useState("");
  const [currentTranslationObjective, setCurrentTranslationObjective] =
    useState<{ [key: string]: string }>({});
  const [currentTranslationHighlight, setCurrentTranslationHighlight] =
    useState<{ [key: string]: string }>({});
  const [currentTranslationOutcome, setCurrentTranslationOutcome] = useState<{
    [key: string]: string;
  }>({});
  const [videoUrl, setVideoUrl] = useState("");
  const [videoSource, setVideoSource] = useState("");
  const [validationError, setValidationError] = useState("");
  const [autoGenerateSlugs, setAutoGenerateSlugs] = useState<{
    [key: string]: boolean;
  }>({});

  // Auto-generate slug from title
  const generateSlug = (text: string): string => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  // Auto-generate slugs when titles change
  const translationTitles = formData.translations.map((t) => t.title).join(",");
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((translation) => {
        if (
          autoGenerateSlugs[translation.locale] &&
          translation.title &&
          !translation.slug
        ) {
          return {
            ...translation,
            slug: generateSlug(translation.title),
          };
        }
        return translation;
      }),
    }));
  }, [translationTitles, autoGenerateSlugs]);

  // Enable auto-generation when title is first entered
  const handleTitleChange = (locale: string, value: string) => {
    if (
      !autoGenerateSlugs[locale] &&
      value &&
      !formData.translations.find((t) => t.locale === locale)?.slug
    ) {
      setAutoGenerateSlugs((prev) => ({ ...prev, [locale]: true }));
    }
    handleTranslationChange(locale, "title", value);
  };

  // Populate form when event data is loaded
  useEffect(() => {
    if (event) {
      // Format dates for DateTimePicker (YYYY-MM-DDTHH:mm format)
      const formatDateTime = (dateString?: string): string => {
        if (!dateString) return "";
        try {
          const date = new Date(dateString);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch {
          return "";
        }
      };

      // Ensure status is one of the valid values
      const validStatuses = ["upcoming", "ongoing", "completed"];
      const eventStatus =
        event.status && validStatuses.includes(event.status)
          ? event.status
          : "upcoming";

      const newFormData = {
        starts_at: formatDateTime(event.starts_at),
        ends_at: formatDateTime(event.ends_at),
        venue: event.location || "",
        ics_uid: "",
        status: eventStatus,
        category: event.category || "",
        duration: event.duration || "",
        organizer: event.organizer || "",
        attendees: event.attendees || 0,
        registration_link: event.registrationLink || "",
        detailed_description: event.detailedDescription || "",
        objectives: event.objectives || [],
        highlights: event.highlights || [],
        outcomes: event.outcomes || [],
        event_report: event.eventReport || "",
        gallery: event.gallery || [],
        videos: event.videos || [],
        translations: ACCEPTABLE_LANGUAGES.map((lang) => {
          const langTranslation = event.translations?.find(
            (t) => t.locale === lang
          );
          return {
            locale: lang,
            title: langTranslation?.title || "",
            description: langTranslation?.description || "",
            slug: langTranslation?.slug || "",
            detailed_description: langTranslation?.detailed_description || "",
            objectives: langTranslation?.objectives || [],
            highlights: langTranslation?.highlights || [],
            outcomes: langTranslation?.outcomes || [],
            event_report: langTranslation?.event_report || "",
          };
        }),
      };
      setFormData(newFormData);

      // Initialize autoGenerateSlugs based on existing slugs
      const initialAutoGenerateSlugs: { [key: string]: boolean } = {};
      newFormData.translations.forEach((t) => {
        // If title exists but slug doesn't, enable auto-gen
        // Otherwise, disable auto-gen (slug was manually set or exists)
        if (t.title && !t.slug) {
          initialAutoGenerateSlugs[t.locale] = true;
        } else {
          initialAutoGenerateSlugs[t.locale] = false;
        }
      });
      setAutoGenerateSlugs(initialAutoGenerateSlugs);
    }
  }, [event]);

  const handleInputChange = (
    field: keyof CreateEventData,
    value: string | number | string[] | EventVideo[]
  ) => {
    setFormData((prev) => {
      // Special handling for status to ensure it's always a valid value
      if (field === "status") {
        const validStatuses = ["upcoming", "ongoing", "completed"];
        const trimmedValue = String(value).trim();
        const validValue = validStatuses.includes(trimmedValue)
          ? trimmedValue
          : "upcoming";
        return { ...prev, [field]: validValue };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleTranslationChange = (
    locale: string,
    field: keyof EventTranslation,
    value: string | string[]
  ) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((translation) =>
        translation.locale === locale
          ? { ...translation, [field]: value }
          : translation
      ),
    }));
  };

  const addObjective = () => {
    if (currentObjective.trim()) {
      setFormData((prev) => ({
        ...prev,
        objectives: [...(prev.objectives || []), currentObjective.trim()],
      }));
      setCurrentObjective("");
    }
  };

  const removeObjective = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      objectives: prev.objectives?.filter((_, i) => i !== index) || [],
    }));
  };

  const addHighlight = () => {
    if (currentHighlight.trim()) {
      setFormData((prev) => ({
        ...prev,
        highlights: [...(prev.highlights || []), currentHighlight.trim()],
      }));
      setCurrentHighlight("");
    }
  };

  const removeHighlight = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      highlights: prev.highlights?.filter((_, i) => i !== index) || [],
    }));
  };

  const addOutcome = () => {
    if (currentOutcome.trim()) {
      setFormData((prev) => ({
        ...prev,
        outcomes: [...(prev.outcomes || []), currentOutcome.trim()],
      }));
      setCurrentOutcome("");
    }
  };

  const removeOutcome = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      outcomes: prev.outcomes?.filter((_, i) => i !== index) || [],
    }));
  };

  const addTranslationObjective = (locale: string) => {
    const current = currentTranslationObjective[locale] || "";
    if (current.trim()) {
      setFormData((prev) => ({
        ...prev,
        translations: prev.translations.map((translation) =>
          translation.locale === locale
            ? {
                ...translation,
                objectives: [...(translation.objectives || []), current.trim()],
              }
            : translation
        ),
      }));
      setCurrentTranslationObjective((prev) => ({ ...prev, [locale]: "" }));
    }
  };

  const removeTranslationObjective = (locale: string, index: number) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((translation) =>
        translation.locale === locale
          ? {
              ...translation,
              objectives:
                translation.objectives?.filter((_, i) => i !== index) || [],
            }
          : translation
      ),
    }));
  };

  const addTranslationHighlight = (locale: string) => {
    const current = currentTranslationHighlight[locale] || "";
    if (current.trim()) {
      setFormData((prev) => ({
        ...prev,
        translations: prev.translations.map((translation) =>
          translation.locale === locale
            ? {
                ...translation,
                highlights: [...(translation.highlights || []), current.trim()],
              }
            : translation
        ),
      }));
      setCurrentTranslationHighlight((prev) => ({ ...prev, [locale]: "" }));
    }
  };

  const removeTranslationHighlight = (locale: string, index: number) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((translation) =>
        translation.locale === locale
          ? {
              ...translation,
              highlights:
                translation.highlights?.filter((_, i) => i !== index) || [],
            }
          : translation
      ),
    }));
  };

  const addTranslationOutcome = (locale: string) => {
    const current = currentTranslationOutcome[locale] || "";
    if (current.trim()) {
      setFormData((prev) => ({
        ...prev,
        translations: prev.translations.map((translation) =>
          translation.locale === locale
            ? {
                ...translation,
                outcomes: [...(translation.outcomes || []), current.trim()],
              }
            : translation
        ),
      }));
      setCurrentTranslationOutcome((prev) => ({ ...prev, [locale]: "" }));
    }
  };

  const removeTranslationOutcome = (locale: string, index: number) => {
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.map((translation) =>
        translation.locale === locale
          ? {
              ...translation,
              outcomes:
                translation.outcomes?.filter((_, i) => i !== index) || [],
            }
          : translation
      ),
    }));
  };

  const addVideo = () => {
    if (videoUrl.trim() && videoSource.trim()) {
      const newVideo: EventVideo = {
        url: videoUrl.trim(),
        source: videoSource.trim(),
      };
      setFormData((prev) => ({
        ...prev,
        videos: [...(prev.videos || []), newVideo],
      }));
      setVideoUrl("");
      setVideoSource("");
    }
  };

  const removeVideo = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      videos: prev.videos?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleGalleryUpload = async (files: File[]): Promise<string[]> => {
    // Convert files to base64 in order
    const base64Promises = files.map((file, index) => {
      return new Promise<{ base64: string; index: number }>(
        (resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            resolve({ base64: result, index });
          };
          reader.onerror = (error) => {
            console.error(`Failed to read file ${file.name}:`, error);
            reject(error);
          };
          reader.readAsDataURL(file);
        }
      );
    });

    try {
      const results = await Promise.all(base64Promises);
      // Sort by index to maintain order
      results.sort((a, b) => a.index - b.index);
      const base64Strings = results.map((r) => r.base64);

      // Update formData with new images - append to existing gallery
      setFormData((prev) => {
        const existingGallery = prev.gallery || [];
        // Append new images to existing ones (ImageUpload component handles deduplication)
        const newGallery = [...existingGallery, ...base64Strings];
        return {
          ...prev,
          gallery: newGallery,
        };
      });

      // Return base64 strings in the same order as input files
      return base64Strings;
    } catch (error) {
      console.error("Error processing gallery upload:", error);
      throw error;
    }
  };

  const handleGalleryChange = (urls: string | string[]) => {
    const urlArray = Array.isArray(urls) ? urls : [urls].filter(Boolean);
    // Only update if the array actually changed to prevent unnecessary re-renders
    setFormData((prev) => {
      const currentGallery = prev.gallery || [];
      const hasChanged =
        urlArray.length !== currentGallery.length ||
        urlArray.some((url, idx) => url !== currentGallery[idx]);

      if (hasChanged) {
        return {
          ...prev,
          gallery: urlArray,
        };
      }
      return prev;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError("");

    if (!id) return;

    // Validate and normalize status - ensure it's always a valid value
    const validStatuses = ["upcoming", "ongoing", "completed"];
    const statusValue = (formData.status || "upcoming").trim();
    const finalStatus = validStatuses.includes(statusValue)
      ? statusValue
      : "upcoming";

    // Validate that ends_at is after starts_at
    if (formData.starts_at && formData.ends_at) {
      const startsAt = new Date(formData.starts_at);
      const endsAt = new Date(formData.ends_at);

      if (endsAt <= startsAt) {
        setValidationError(t("admin.events.validation.endsAtAfterStartsAt"));
        return;
      }
    }

    // Validate at least one translation has title and slug
    const hasValidTranslation = formData.translations.some(
      (t) => t.title && t.slug
    );
    if (!hasValidTranslation) {
      setValidationError(t("admin.events.validation.requiredTranslation"));
      return;
    }

    // Prepare submission data with validated status
    // Ensure status is always valid
    const submissionData: Partial<CreateEventData> = {
      ...formData,
      status: finalStatus, // Always ensure status is valid
    };

    try {
      await updateEventMutation.mutateAsync({
        id: Number(id),
        eventData: submissionData,
      });
      navigate(`/${currentLanguage}/admin/events`);
    } catch (error: unknown) {
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { errors?: Record<string, string[]> } };
        };
        if (axiosError.response?.data?.errors) {
          const errors = axiosError.response.data.errors;
          const firstError = Object.values(errors).flat()[0];
          setValidationError(
            firstError || t("admin.events.validation.genericError")
          );
        } else {
          setValidationError(t("admin.events.validation.genericError"));
        }
      } else if (error instanceof Error) {
        setValidationError(
          error.message || t("admin.events.validation.genericError")
        );
      } else {
        setValidationError(t("admin.events.validation.genericError"));
      }
    }
  };

  if (isLoadingEvent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            {t("common.loading")}
          </p>
        </div>
      </div>
    );
  }

  if (isErrorEvent || (!isLoadingEvent && !event)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
        <div className="max-w-4xl mx-auto text-center py-16">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            {t("admin.events.eventNotFound")}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t("admin.events.eventNotFoundDescription")}
          </p>
          <Button onClick={() => navigate(`/${currentLanguage}/admin/events`)}>
            {t("admin.events.backToEvents")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-6"
        >
          <Button
            variant="outline"
            onClick={() => navigate(`/${currentLanguage}/admin/events`)}
            className="flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.back")}
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              {t("admin.events.editEvent")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t("admin.events.editSubtitle")}
            </p>
          </div>
        </motion.div>

        {/* Validation Error */}
        {validationError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-600 dark:text-red-400 flex-1">
              {validationError}
            </p>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                  <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  {t("admin.events.basicInfo")}
                </CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t("admin.events.basicInfoHelp")}
                </p>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="starts_at"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      {t("admin.events.startsAt")} *
                    </Label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("admin.events.startsAtHelp")}
                    </p>
                    <DateTimePicker
                      id="starts_at"
                      value={formData.starts_at}
                      onChange={(value) => {
                        handleInputChange("starts_at", value);
                        // Clear ends_at if it's before the new start time
                        if (
                          formData.ends_at &&
                          value &&
                          new Date(formData.ends_at) <= new Date(value)
                        ) {
                          handleInputChange("ends_at", "");
                        }
                      }}
                      placeholder={t("admin.events.selectStartDate")}
                      showTimeSelect
                      dateFormat="MMM dd, yyyy h:mm aa"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="ends_at"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      {t("admin.events.endsAt")} *
                    </Label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("admin.events.endsAtHelp")}
                    </p>
                    <DateTimePicker
                      id="ends_at"
                      value={formData.ends_at}
                      onChange={(value) => handleInputChange("ends_at", value)}
                      placeholder={t("admin.events.selectEndDate")}
                      showTimeSelect
                      dateFormat="MMM dd, yyyy h:mm aa"
                      minDate={
                        formData.starts_at
                          ? new Date(formData.starts_at)
                          : undefined
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="venue"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <MapPin className="w-4 h-4 text-green-600 dark:text-green-400" />
                      {t("admin.events.venue")}
                    </Label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("admin.events.venueHelp")}
                    </p>
                    <Input
                      id="venue"
                      value={formData.venue}
                      onChange={(e) =>
                        handleInputChange("venue", e.target.value)
                      }
                      placeholder={t("admin.events.venuePlaceholder")}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="status"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <Info className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                      {t("admin.events.status")} *
                    </Label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("admin.events.statusHelp")}
                    </p>
                    <select
                      id="status"
                      value={formData.status || "upcoming"}
                      onChange={(e) =>
                        handleInputChange("status", e.target.value)
                      }
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="upcoming">
                        {t("events.status.upcoming")}
                      </option>
                      <option value="ongoing">
                        {t("events.status.ongoing")}
                      </option>
                      <option value="completed">
                        {t("events.status.completed")}
                      </option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="category" className="text-sm font-semibold">
                      {t("admin.events.category")}
                    </Label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("admin.events.categoryHelp")}
                    </p>
                    <Input
                      id="category"
                      value={formData.category}
                      onChange={(e) =>
                        handleInputChange("category", e.target.value)
                      }
                      placeholder={t("admin.events.categoryPlaceholder")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="duration" className="text-sm font-semibold">
                      {t("admin.events.duration")}
                    </Label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("admin.events.durationHelp")}
                    </p>
                    <Input
                      id="duration"
                      value={formData.duration}
                      onChange={(e) =>
                        handleInputChange("duration", e.target.value)
                      }
                      placeholder={t("admin.events.durationPlaceholder")}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label
                      htmlFor="organizer"
                      className="text-sm font-semibold"
                    >
                      {t("admin.events.organizer")}
                    </Label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("admin.events.organizerHelp")}
                    </p>
                    <Input
                      id="organizer"
                      value={formData.organizer}
                      onChange={(e) =>
                        handleInputChange("organizer", e.target.value)
                      }
                      placeholder={t("admin.events.organizerPlaceholder")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="attendees"
                      className="flex items-center gap-2 text-sm font-semibold"
                    >
                      <Users className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                      {t("admin.events.attendees")}
                    </Label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t("admin.events.attendeesHelp")}
                    </p>
                    <Input
                      id="attendees"
                      type="number"
                      min="0"
                      value={formData.attendees}
                      onChange={(e) =>
                        handleInputChange(
                          "attendees",
                          parseInt(e.target.value) || 0
                        )
                      }
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="registration_link"
                    className="flex items-center gap-2 text-sm font-semibold"
                  >
                    <LinkIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    {t("admin.events.registrationLink")}
                  </Label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("admin.events.registrationLinkHelp")}
                  </p>
                  <Input
                    id="registration_link"
                    type="url"
                    value={formData.registration_link}
                    onChange={(e) =>
                      handleInputChange("registration_link", e.target.value)
                    }
                    placeholder="https://example.com/register"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="detailed_description"
                    className="text-sm font-semibold"
                  >
                    {t("admin.events.detailedDescription")}
                  </Label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t("admin.events.detailedDescriptionHelp")}
                  </p>
                  <textarea
                    id="detailed_description"
                    value={formData.detailed_description}
                    onChange={(e) =>
                      handleInputChange("detailed_description", e.target.value)
                    }
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder={t(
                      "admin.events.detailedDescriptionPlaceholder"
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Objectives, Highlights, Outcomes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {/* Objectives */}
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {t("admin.events.objectives")}
                </CardTitle>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t("admin.events.objectivesHelp")}
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="flex gap-2">
                  <Input
                    placeholder={t("admin.events.addObjective")}
                    value={currentObjective}
                    onChange={(e) => setCurrentObjective(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addObjective())
                    }
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={addObjective}
                    className="shrink-0"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {formData.objectives?.map((objective, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-2"
                    >
                      <Badge
                        variant="secondary"
                        className="flex-1 justify-start text-xs py-1.5 px-2"
                      >
                        {objective}
                      </Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeObjective(index)}
                        className="shrink-0 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Highlights */}
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {t("admin.events.highlights")}
                </CardTitle>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t("admin.events.highlightsHelp")}
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="flex gap-2">
                  <Input
                    placeholder={t("admin.events.addHighlight")}
                    value={currentHighlight}
                    onChange={(e) => setCurrentHighlight(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addHighlight())
                    }
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={addHighlight}
                    className="shrink-0"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {formData.highlights?.map((highlight, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-2"
                    >
                      <Badge
                        variant="secondary"
                        className="flex-1 justify-start text-xs py-1.5 px-2"
                      >
                        {highlight}
                      </Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeHighlight(index)}
                        className="shrink-0 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Outcomes */}
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {t("admin.events.outcomes")}
                </CardTitle>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t("admin.events.outcomesHelp")}
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="flex gap-2">
                  <Input
                    placeholder={t("admin.events.addOutcome")}
                    value={currentOutcome}
                    onChange={(e) => setCurrentOutcome(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addOutcome())
                    }
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={addOutcome}
                    className="shrink-0"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {formData.outcomes?.map((outcome, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-2"
                    >
                      <Badge
                        variant="secondary"
                        className="flex-1 justify-start text-xs py-1.5 px-2"
                      >
                        {outcome}
                      </Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOutcome(index)}
                        className="shrink-0 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Gallery & Videos */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Gallery */}
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {t("admin.events.gallery")}
                </CardTitle>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t("admin.events.galleryHelp")}
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <ImageUpload
                  onUpload={handleGalleryUpload}
                  onChange={handleGalleryChange}
                  value={formData.gallery}
                  multiple
                  maxFiles={20}
                />
              </CardContent>
            </Card>

            {/* Videos */}
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {t("admin.events.videos")}
                </CardTitle>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t("admin.events.videosHelp")}
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="space-y-3">
                  <Input
                    placeholder={t("admin.events.videoUrl")}
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                  />
                  <Input
                    placeholder={t("admin.events.videoSource")}
                    value={videoSource}
                    onChange={(e) => setVideoSource(e.target.value)}
                  />
                  <Button
                    type="button"
                    onClick={addVideo}
                    className="w-full"
                    disabled={!videoUrl.trim() || !videoSource.trim()}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t("admin.events.addVideo")}
                  </Button>
                </div>
                {formData.videos && formData.videos.length > 0 && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {formData.videos.map((video, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-2 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {video.source}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {video.url}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeVideo(index)}
                          className="shrink-0 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Translations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border-b border-gray-200 dark:border-gray-700">
                <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                  <Info className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  {t("admin.events.translations")}
                </CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t("admin.events.translationsHelp")}
                </p>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                {ACCEPTABLE_LANGUAGES.map((lang, langIndex) => {
                  const translation = formData.translations.find(
                    (t) => t.locale === lang
                  );
                  if (!translation) return null;

                  return (
                    <motion.div
                      key={lang}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + langIndex * 0.1 }}
                      className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-gradient-to-br from-white to-gray-50/50 dark:from-gray-800 dark:to-gray-900/50"
                    >
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-bold">
                            {lang.toUpperCase()}
                          </span>
                          {lang === "en"
                            ? "English"
                            : lang === "fr"
                            ? "Français"
                            : lang}
                        </h3>
                        {translation.title && translation.slug && (
                          <Badge
                            variant="default"
                            className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            {t("admin.events.complete")}
                          </Badge>
                        )}
                      </div>
                      <div className="space-y-5">
                        <div className="space-y-2">
                          <Label
                            htmlFor={`title-${lang}`}
                            className="flex items-center gap-2 text-sm font-semibold"
                          >
                            {t("admin.events.title")} *
                            <div title={t("admin.events.titleHelp")}>
                              <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                            </div>
                          </Label>
                          <Input
                            id={`title-${lang}`}
                            value={translation.title}
                            onChange={(e) =>
                              handleTitleChange(lang, e.target.value)
                            }
                            placeholder={t("admin.events.titlePlaceholder")}
                            required
                            className="w-full"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor={`slug-${lang}`}
                            className="flex items-center gap-2 text-sm font-semibold"
                          >
                            {t("admin.events.slug")} *
                            <div title={t("admin.events.slugHelp")}>
                              <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                            </div>
                          </Label>
                          <div className="relative">
                            <Input
                              id={`slug-${lang}`}
                              value={translation.slug}
                              onChange={(e) => {
                                handleTranslationChange(
                                  lang,
                                  "slug",
                                  e.target.value
                                );
                                // Disable auto-generation if user manually edits
                                setAutoGenerateSlugs((prev) => ({
                                  ...prev,
                                  [lang]: false,
                                }));
                              }}
                              placeholder={t("admin.events.slugPlaceholder")}
                              required
                              className="w-full font-mono text-sm"
                            />
                            {autoGenerateSlugs[lang] && (
                              <Badge
                                variant="secondary"
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs"
                              >
                                {t("admin.events.autoGenerated")}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t("admin.events.slugDescription")}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor={`description-${lang}`}
                            className="text-sm font-semibold"
                          >
                            {t("admin.events.description")}
                          </Label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t("admin.events.descriptionHelp")}
                          </p>
                          <textarea
                            id={`description-${lang}`}
                            value={translation.description}
                            onChange={(e) =>
                              handleTranslationChange(
                                lang,
                                "description",
                                e.target.value
                              )
                            }
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            placeholder={t(
                              "admin.events.descriptionPlaceholder"
                            )}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor={`detailed_description-${lang}`}
                            className="text-sm font-semibold"
                          >
                            {t("admin.events.detailedDescription")}
                          </Label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t("admin.events.detailedDescriptionHelp")}
                          </p>
                          <textarea
                            id={`detailed_description-${lang}`}
                            value={translation.detailed_description}
                            onChange={(e) =>
                              handleTranslationChange(
                                lang,
                                "detailed_description",
                                e.target.value
                              )
                            }
                            rows={5}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            placeholder={t(
                              "admin.events.detailedDescriptionPlaceholder"
                            )}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor={`objectives-${lang}`}
                            className="text-sm font-semibold"
                          >
                            {t("admin.events.objectives")}
                          </Label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t("admin.events.objectivesHelp")}
                          </p>
                          <div className="flex gap-2 mb-2">
                            <Input
                              placeholder={t("admin.events.addObjective")}
                              value={currentTranslationObjective[lang] || ""}
                              onChange={(e) =>
                                setCurrentTranslationObjective((prev) => ({
                                  ...prev,
                                  [lang]: e.target.value,
                                }))
                              }
                              onKeyPress={(e) =>
                                e.key === "Enter" &&
                                (e.preventDefault(),
                                addTranslationObjective(lang))
                              }
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              onClick={() => addTranslationObjective(lang)}
                              className="shrink-0"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {translation.objectives?.map((objective, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="flex items-center gap-2"
                              >
                                <Badge
                                  variant="secondary"
                                  className="flex-1 justify-start text-xs py-1.5 px-2"
                                >
                                  {objective}
                                </Badge>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    removeTranslationObjective(lang, index)
                                  }
                                  className="shrink-0 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor={`highlights-${lang}`}
                            className="text-sm font-semibold"
                          >
                            {t("admin.events.highlights")}
                          </Label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t("admin.events.highlightsHelp")}
                          </p>
                          <div className="flex gap-2 mb-2">
                            <Input
                              placeholder={t("admin.events.addHighlight")}
                              value={currentTranslationHighlight[lang] || ""}
                              onChange={(e) =>
                                setCurrentTranslationHighlight((prev) => ({
                                  ...prev,
                                  [lang]: e.target.value,
                                }))
                              }
                              onKeyPress={(e) =>
                                e.key === "Enter" &&
                                (e.preventDefault(),
                                addTranslationHighlight(lang))
                              }
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              onClick={() => addTranslationHighlight(lang)}
                              className="shrink-0"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {translation.highlights?.map((highlight, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="flex items-center gap-2"
                              >
                                <Badge
                                  variant="secondary"
                                  className="flex-1 justify-start text-xs py-1.5 px-2"
                                >
                                  {highlight}
                                </Badge>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    removeTranslationHighlight(lang, index)
                                  }
                                  className="shrink-0 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor={`outcomes-${lang}`}
                            className="text-sm font-semibold"
                          >
                            {t("admin.events.outcomes")}
                          </Label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t("admin.events.outcomesHelp")}
                          </p>
                          <div className="flex gap-2 mb-2">
                            <Input
                              placeholder={t("admin.events.addOutcome")}
                              value={currentTranslationOutcome[lang] || ""}
                              onChange={(e) =>
                                setCurrentTranslationOutcome((prev) => ({
                                  ...prev,
                                  [lang]: e.target.value,
                                }))
                              }
                              onKeyPress={(e) =>
                                e.key === "Enter" &&
                                (e.preventDefault(),
                                addTranslationOutcome(lang))
                              }
                              className="flex-1"
                            />
                            <Button
                              type="button"
                              onClick={() => addTranslationOutcome(lang)}
                              className="shrink-0"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {translation.outcomes?.map((outcome, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="flex items-center gap-2"
                              >
                                <Badge
                                  variant="secondary"
                                  className="flex-1 justify-start text-xs py-1.5 px-2"
                                >
                                  {outcome}
                                </Badge>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    removeTranslationOutcome(lang, index)
                                  }
                                  className="shrink-0 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor={`event_report-${lang}`}
                            className="text-sm font-semibold"
                          >
                            {t("admin.events.eventReport")}
                          </Label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t("admin.events.eventReportHelp")}
                          </p>
                          <textarea
                            id={`event_report-${lang}`}
                            value={translation.event_report || ""}
                            onChange={(e) =>
                              handleTranslationChange(
                                lang,
                                "event_report",
                                e.target.value
                              )
                            }
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            placeholder={t(
                              "admin.events.eventReportPlaceholder"
                            )}
                          />
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </CardContent>
            </Card>
          </motion.div>

          {/* Submit */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700"
          >
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/${currentLanguage}/admin/events`)}
              className="px-6"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={updateEventMutation.isPending}
              className="px-8 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {updateEventMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t("common.updating")}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  {t("admin.events.update")}
                </span>
              )}
            </Button>
          </motion.div>
        </form>
      </div>
    </div>
  );
};

EventEdit.displayName = "EventEdit";

export default EventEdit;
